import { memo } from 'react'
import * as Sentry from '@sentry/electron/renderer'
import { HistoryRow } from '@renderer/overlay/Sidepanel/components/history/history-row'
import { HistoryListSkeleton } from '@renderer/overlay/Sidepanel/components/history/history-list-skeleton'
import { useOpenActivity } from '@renderer/overlay/Sidepanel/components/history/use-open-activity'
import { useHistoryActivity } from '@renderer/overlay/Sidepanel/hooks/use-history-activity'
import { useIsSidepanelSectionCollapsed } from '../../../atoms'
import { SidepanelSectionViewMore } from '../nested/section-view-more'
import { absorbRemainingItems, useProgressiveSectionLimit } from '../use-progressive-section-limit'

const HISTORY_INITIAL_LIMIT = 5

// Mirrors the History view's list geometry exactly: there, a `px-3` scroller
// wraps rows that carry their own `px-3`, so row content sits 12px inside the
// header's gutter and the hover fill stops short of the panel edge. Home's
// section padding (`p-3`) is the scroller's counterpart, so the rows need no
// inset of their own — only the tighter row gap, since the section's own
// `gap-1.5` is sized for cards.
const ROW_LIST_CLASS = 'flex flex-col gap-[2px]'

export const SidepanelHistorySection = memo(function SidepanelHistorySection() {
  const handleItemClick = useOpenActivity('Home')
  // Unfiltered preview — same query cache as the History view's "All" feed
  // (shared page size), so the view opens on already-fetched data.
  const { items, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useHistoryActivity({})
  const isCollapsed = useIsSidepanelSectionCollapsed('history')
  const { visibleCount, nextVisibleCount, showMore } = useProgressiveSectionLimit(
    'history',
    HISTORY_INITIAL_LIMIT,
    isCollapsed,
  )
  const visibleItemCount = hasNextPage ? visibleCount : absorbRemainingItems(visibleCount, items.length)
  const displayedItems = items.slice(0, visibleItemCount)
  const canShowMore = visibleItemCount < items.length || hasNextPage

  if (isLoading && !items.length) {
    // The skeleton already mirrors the row geometry, gaps included.
    return <HistoryListSkeleton count={visibleCount} />
  }

  // No empty copy by design — with no history at all the whole section is
  // hidden (see SidepanelSections' `useHasHistoryActivity`).
  if (!items.length) {
    return null
  }

  const handleViewMore = () => {
    showMore()
    if (nextVisibleCount >= items.length && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage().catch((error) => Sentry.captureException(error))
    }
  }

  return (
    <div className={ROW_LIST_CLASS}>
      {displayedItems.map((item) => (
        <HistoryRow key={`${item.type}:${item.id}`} item={item} onOpen={handleItemClick} />
      ))}
      {canShowMore && <SidepanelSectionViewMore onClick={handleViewMore} disabled={isFetchingNextPage} />}
    </div>
  )
})
